// src/pages/AdminOnboardingApplications.tsx: admin review and approval for
// allocator, service provider and candidate applications.
//
// The applicant's auth user already exists, created by their own signUp during
// the apply flow, so approving does not create an account. It activates the
// existing profile and creates the domain record (firm / service_providers /
// candidates) that the rest of the platform reads.
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabase';
import { C, fmtDate, fmtDateTime, sendMail } from '../lib/applications';
import {
  APPLICATION_TABLES, ApplicantKind, ONBOARDING_STATUS_LABELS,
  AllocatorApplication, ProviderApplication, CandidateApplication,
  organisationTypeLabel, aumRangeLabel, allocationLabel, ticketSizeLabel,
  qualificationLabel, assetClassLabels, clientTypeLabels, pricingModelLabel,
  seniorityLevelLabel,
} from '../lib/onboardingApplications';

type Kind = Exclude<ApplicantKind, 'manager'>;

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '8px 10px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
  padding: '9px 18px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '8px 15px', fontSize: '12px', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'system-ui',
};

const statusTone = (status: string) => {
  switch (status) {
    case 'approved':     return { fg: C.success, bg: '#F0FDF4', bd: '#BBF7D0' };
    case 'declined':     return { fg: C.danger,  bg: '#FEF2F2', bd: '#FECACA' };
    case 'admin_review': return { fg: C.blue,    bg: '#EFF6FF', bd: '#BFDBFE' };
    default:             return { fg: C.muted,   bg: C.surface, bd: C.border };
  }
};

function Badge({ text, tone }: { text: string; tone: { fg: string; bg: string; bd: string } }) {
  return (
    <span style={{
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.bd}`,
      fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px',
      letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>{text}</span>
  );
}

const subjectOf = (kind: Kind, a: any): string =>
  kind === 'allocator' ? a.organisation_name
    : kind === 'provider' ? a.company_name
      : a.contact_name;

const subtitleOf = (kind: Kind, a: any): string =>
  kind === 'allocator' ? organisationTypeLabel(a.organisation_type)
    : kind === 'provider' ? a.category
      : [a.current_role, seniorityLevelLabel(a.seniority)].filter(Boolean).join(' · ');

export default function AdminOnboardingApplications({ kind }: { kind: Kind }) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    const { data, error: err } = await supabase.from(APPLICATION_TABLES[kind])
      .select('*').order('created_at', { ascending: false });
    if (err) { setError(err.message); setLoading(false); return; }
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (q && !`${subjectOf(kind, r)} ${r.contact_name} ${r.contact_email}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, statusFilter, search, kind]);

  return (
    <div>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '14px', fontSize: '12.5px', color: C.danger }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name or email…" style={{ ...inputStyle, maxWidth: '250px' }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, maxWidth: '180px', cursor: 'pointer' }}>
          <option value="all">All statuses</option>
          {Object.entries(ONBOARDING_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span style={{ fontSize: '12px', color: C.muted, fontWeight: 600 }}>
          {filtered.length} application{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading applications…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No applications</div>
          <div style={{ fontSize: '13px', color: C.muted }}>
            {rows.length === 0 ? 'No one has applied yet.' : 'Nothing matches these filters.'}
          </div>
        </div>
      ) : (
        <div style={{ ...card, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
            <thead>
              <tr style={{ background: C.navy }}>
                {['Name', 'Contact', 'Type', 'Status', 'Submitted', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontSize: '9.5px', color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${C.borderLt}`, background: i % 2 === 0 ? C.white : C.surface }}>
                  <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 600, color: C.navy }}>{subjectOf(kind, a)}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textSec }}>
                    <div>{a.contact_name}</div>
                    <div style={{ color: C.muted, fontSize: '11.5px' }}>{a.contact_email}</div>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textSec }}>{subtitleOf(kind, a)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge text={ONBOARDING_STATUS_LABELS[a.status] || a.status} tone={statusTone(a.status)} />
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '11.5px', color: C.muted }}>{fmtDate(a.created_at)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => setSelected(a)} style={{ ...btnGhost, padding: '5px 12px' }}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <Detail
          kind={kind}
          app={selected}
          onClose={() => setSelected(null)}
          onChanged={updated => {
            setRows(prev => prev.map(r => r.id === updated.id ? updated : r));
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}

// == Detail and decisions =====================================================

function Detail({ kind, app, onClose, onChanged }: {
  kind: Kind; app: any; onClose: () => void; onChanged: (a: any) => void;
}) {
  const [notes, setNotes] = useState(app.admin_notes || '');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const table = APPLICATION_TABLES[kind];

  const patch = async (fields: Record<string, any>, label: string) => {
    setBusy(label); setMsg(null);
    const { data, error } = await supabase.from(table)
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', app.id).select().single();
    setBusy('');
    if (error) { setMsg({ type: 'err', text: error.message }); return null; }
    onChanged(data);
    return data;
  };

  const saveNotes = async () => {
    if (await patch({ admin_notes: notes }, 'notes')) setMsg({ type: 'ok', text: 'Notes saved.' });
  };

  const decline = async () => {
    if (!window.confirm(`Decline the application from ${subjectOf(kind, app)}? The applicant will be emailed.`)) return;
    const r = await patch({ status: 'declined', admin_notes: notes, reviewed_at: new Date().toISOString() }, 'decline');
    if (!r) return;
    await sendMail('onboarding_application_declined', {
      contact_email: app.contact_email, contact_name: app.contact_name,
      subject_name: subjectOf(kind, app),
    });
    setMsg({ type: 'ok', text: 'Application declined.' });
  };

  /**
   * Activate the applicant. The auth user already exists; this flips the
   * profile to approved and creates the record the platform reads for that
   * role. If the domain record fails, the application is left un-approved so
   * the admin can retry rather than leaving a half-activated account.
   */
  const approve = async () => {
    if (!app.profile_id) {
      setMsg({ type: 'err', text: 'This application has no linked account, so it cannot be activated.' });
      return;
    }
    if (!window.confirm(`Approve ${subjectOf(kind, app)} and activate their account?`)) return;
    setBusy('approve'); setMsg(null);

    try {
      let linkField: Record<string, any> = {};

      if (kind === 'allocator') {
        const a = app as AllocatorApplication;
        const { data: firm, error: firmErr } = await supabase.from('firms').insert({
          name: a.organisation_name,
          type: 'allocator',
          description: a.organisation_description,
          headquarters: [a.headquarters_city, a.headquarters_country].filter(Boolean).join(', '),
          website: a.organisation_website,
          owner_id: a.profile_id,
        }).select().single();
        if (firmErr) throw firmErr;
        linkField = { firm_id: firm.id };
      }

      if (kind === 'provider') {
        const p = app as ProviderApplication;
        const { data: provider, error: provErr } = await supabase.from('service_providers').insert({
          owner_id: p.profile_id,
          name: p.company_name,
          category: p.category,
          description: p.company_description,
          headquarters_country: p.headquarters_country,
          headquarters_city: p.headquarters_city,
          regions_covered: p.regions_covered || [],
          website: p.company_website,
          founded_year: p.founded_year,
          client_count: p.client_count,
          logo_url: p.logo_url,
          demo_video_url: p.demo_video_url,
          account_status: 'active',
        }).select().single();
        if (provErr) throw provErr;
        linkField = { provider_id: provider.id };
      }

      if (kind === 'candidate') {
        const c = app as CandidateApplication;
        const { data: candidate, error: candErr } = await supabase.from('candidates').insert({
          owner_id: c.profile_id,
          full_name: c.contact_name,
          current_role: c.current_role,
          current_employer: c.current_employer,
          hide_employer: c.hide_employer,
          description: c.bio,
          years_experience: c.years_experience,
          seniority: c.seniority,
          target_roles: c.target_roles || [],
          asset_classes: c.asset_classes || [],
          skills: c.skills || [],
          relocation_countries: c.relocation_countries || [],
          cv_url: c.cv_url,
          visible_on_platform: true,
          profile_complete: !!(c.contact_name && c.current_role && c.seniority && (c.target_roles || []).length),
        }).select().single();
        if (candErr) throw candErr;
        linkField = { candidate_id: candidate.id };
      }

      const roleForKind: Record<Kind, string> = {
        allocator: 'allocator', provider: 'service_provider', candidate: 'candidate',
      };
      const { error: profErr } = await supabase.from('profiles')
        .update({ account_status: 'approved', role: roleForKind[kind] })
        .eq('id', app.profile_id);
      if (profErr) throw profErr;

      const now = new Date().toISOString();
      const { data: saved, error: appErr } = await supabase.from(table).update({
        status: 'approved', admin_notes: notes, reviewed_at: now, updated_at: now, ...linkField,
      }).eq('id', app.id).select().single();
      if (appErr) throw appErr;

      await sendMail(`${kind}_welcome`, {
        contact_email: app.contact_email, contact_name: app.contact_name,
        subject_name: subjectOf(kind, app),
      });

      setBusy('');
      onChanged(saved);
      setMsg({ type: 'ok', text: 'Account activated and welcome email sent.' });
    } catch (e: any) {
      setBusy('');
      setMsg({ type: 'err', text: `Could not activate: ${e?.message || e}. The application is unchanged, so it is safe to retry.` });
    }
  };

  const rows = detailRows(kind, app);

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 20px', zIndex: 200, overflowY: 'auto',
      }}
    >
      <div style={{ background: C.white, borderRadius: '4px', width: '100%', maxWidth: '820px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '22px 26px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          <div>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '21px', color: C.navy, fontWeight: 400, margin: '0 0 6px' }}>
              {subjectOf(kind, app)}
            </h2>
            <Badge text={ONBOARDING_STATUS_LABELS[app.status] || app.status} tone={statusTone(app.status)} />
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: C.mutedLt, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '22px 26px' }}>
          {msg && (
            <div style={{
              background: msg.type === 'ok' ? '#F0FDF4' : '#FEF2F2',
              border: `1px solid ${msg.type === 'ok' ? '#BBF7D0' : '#FECACA'}`,
              borderRadius: '3px', padding: '9px 12px', marginBottom: '16px',
              fontSize: '12.5px', color: msg.type === 'ok' ? C.success : C.danger, lineHeight: 1.6,
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <SectionTitle>Contact</SectionTitle>
              <KV k="Name" v={app.contact_name} />
              <KV k="Email" v={app.contact_email} />
              <KV k="Phone" v={app.contact_phone} />
              <KV k="Heard about us" v={app.heard_about} />
              <KV k="Submitted" v={fmtDateTime(app.created_at)} />
            </div>
            <div>
              <SectionTitle>Application</SectionTitle>
              {rows.map(([k, v]) => <KV key={k} k={k} v={v} />)}
            </div>
          </div>

          {longTextOf(kind, app) && (
            <div style={{ marginTop: '18px' }}>
              <SectionTitle>{kind === 'candidate' ? 'Bio' : 'Description'}</SectionTitle>
              <div style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {longTextOf(kind, app)}
              </div>
            </div>
          )}

          <div style={{ marginTop: '20px' }}>
            <SectionTitle>Admin notes</SectionTitle>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              placeholder="Internal notes. Shown to the applicant on their portal if the application is not approved." />
            <button onClick={saveNotes} disabled={busy === 'notes'} style={{ ...btnGhost, marginTop: '8px' }}>
              {busy === 'notes' ? 'Saving…' : 'Save notes'}
            </button>
          </div>

          <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '22px', paddingTop: '18px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {app.status !== 'approved' ? (
              <>
                <button onClick={decline} disabled={!!busy} style={{ ...btnGhost, color: C.danger, borderColor: `${C.danger}44` }}>Decline</button>
                <button onClick={approve} disabled={!!busy} style={btnPrimary}>
                  {busy === 'approve' ? 'Activating…' : 'Approve and activate'}
                </button>
              </>
            ) : (
              <div style={{ fontSize: '12.5px', color: C.success, fontWeight: 600 }}>
                ✓ Activated{app.reviewed_at ? ` on ${fmtDate(app.reviewed_at)}` : ''}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '10px' }}>{children}</div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '6px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
      <div style={{ width: '150px', flexShrink: 0, color: C.muted }}>{k}</div>
      <div style={{ color: C.text, fontWeight: 500, wordBreak: 'break-word' }}>{v || '-'}</div>
    </div>
  );
}

const longTextOf = (kind: Kind, a: any): string =>
  kind === 'allocator' ? (a.organisation_description || '')
    : kind === 'provider' ? [a.company_description, a.primary_services].filter(Boolean).join('\n\n')
      : (a.bio || '');

function detailRows(kind: Kind, a: any): [string, string][] {
  if (kind === 'allocator') {
    return [
      ['Organisation', a.organisation_name],
      ['Type', organisationTypeLabel(a.organisation_type)],
      ['Headquarters', [a.headquarters_city, a.headquarters_country].filter(Boolean).join(', ')],
      ['Website', a.organisation_website || ''],
      ['Total AUM', aumRangeLabel(a.total_aum_range)],
      ['Alternatives allocation', allocationLabel(a.alternatives_allocation_pct)],
      ['Asset classes', assetClassLabels(a.asset_classes_of_interest)],
      ['Ticket size', ticketSizeLabel(a.typical_ticket_size)],
      ['Professional investor', a.is_professional_investor ? 'Yes' : 'No'],
      ['Qualification', qualificationLabel(a.qualification_basis)],
    ];
  }
  if (kind === 'provider') {
    return [
      ['Company', a.company_name],
      ['Category', a.category],
      ['Headquarters', [a.headquarters_city, a.headquarters_country].filter(Boolean).join(', ')],
      ['Website', a.company_website || ''],
      ['Founded', a.founded_year ? String(a.founded_year) : ''],
      ['Clients', a.client_count != null ? String(a.client_count) : ''],
      ['Regions', (a.regions_covered || []).join(', ')],
      ['Target clients', clientTypeLabels(a.target_client_types)],
      ['Pricing model', pricingModelLabel(a.pricing_model)],
      ['Demo video', a.demo_video_url || ''],
    ];
  }
  return [
    ['Current role', a.current_role || ''],
    ['Employer', a.current_employer ? `${a.current_employer}${a.hide_employer ? ' (hidden)' : ''}` : ''],
    ['Years of experience', a.years_experience != null ? String(a.years_experience) : ''],
    ['Seniority', seniorityLevelLabel(a.seniority)],
    ['Target roles', (a.target_roles || []).join(', ')],
    ['Asset classes', assetClassLabels(a.asset_classes)],
    ['Skills', (a.skills || []).join(', ')],
    ['Relocation', a.open_to_relocation ? ((a.relocation_countries || []).join(', ') || 'Yes') : 'No'],
    ['LinkedIn', a.linkedin_url || ''],
    ['CV', a.cv_url ? 'Uploaded' : 'Not provided'],
  ];
}
